import React, { useState, useEffect } from 'react';
import API_BASE from '../config';

export default function HeroSection() {
    const [form, setForm] = useState({ name: '', phone: '', city: '', service: '' });
    const [cities, setCities] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetch(`${API_BASE}/cities.php`)
            .then(r => r.json())
            .then(data => {
                if (data.status === 'success') setCities(data.cities || []);
            });
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const formData = new FormData();
            Object.keys(form).forEach(key => formData.append(key, form[key]));

            const response = await fetch(`${API_BASE}/contact.php`, {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (data.status === 'success') {
                setSubmitted(true);
                setForm({ name: '', phone: '', city: '', service: '' });
            } else {
                setError(data.message || 'Submission failed. Please try again.');
            }
        } catch (err) {
            setError('Could not connect to the server.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="relative min-h-screen flex items-center" id="home">
            {/* Background Image */}
            <div className="absolute inset-0">
                <img
                    src="https://images.unsplash.com/photo-1618219908412-a29a1bb7b86e?w=1920&q=90"
                    alt="Luxury interior design by Bliv Design Studio"
                    className="w-full h-full object-cover"
                />
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
            </div>

            {/* Content */}
            <div className="relative w-full max-w-7xl mx-auto px-4 pt-32 pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    {/* Left: Headline */}
                    <div>
                        <span className="inline-block bg-[#2E63E8]/20 border border-[#2E63E8]/40 text-[#2E63E8] text-xs font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-widest">
                            Leading Interior Design Studio in South India
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6">
                            Transform Your Space Into A
                            <span className="text-[#2E63E8]"> Masterpiece</span>
                        </h1>
                        <p className="text-gray-300 text-lg leading-relaxed mb-8 max-w-xl">
                            Interior design studio offering end-to-end solutions from ceiling to flooring. Over 500+ luxury projects delivered across South India.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <a href="/contact" className="btn-yellow px-8 py-4 text-base inline-block text-center min-w-[200px]">
                                Get Free Estimate
                            </a>
                            <a href="#our-works" className="border-2 border-white/40 text-white hover:border-[#2E63E8] hover:text-[#2E63E8] font-semibold px-8 py-4 rounded-lg transition-all duration-300 text-base text-center min-w-[200px]">
                                View Our Work
                            </a>
                        </div>
                        {/* Stats */}
                        <div className="flex gap-8 mt-10">
                            {[['500+', 'Projects Done'], ['1000+', 'Happy Clients'], ['10+', 'Years Exp.'], ['5+', 'Cities']].map(([num, label]) => (
                                <div key={label} className="text-center">
                                    <div className="text-2xl font-bold text-white">{num}</div>
                                    <div className="text-gray-400 text-xs mt-1">{label}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: Form Card */}
                    <div className="lg:ml-auto">
                        <div className="bg-white/95 backdrop-blur-md rounded-3xl p-8 shadow-2xl w-full max-w-md mx-auto">
                            <h3 className="font-bold text-gray-900 text-xl mb-1">Book Free Design Session</h3>
                            <p className="text-gray-500 text-sm mb-6">Get a free consultation with our expert designers</p>

                            {submitted ? (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <p className="text-blue-700 font-semibold">Thank you! We'll call you soon.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <input
                                            type="text"
                                            placeholder="Your Full Name"
                                            value={form.name}
                                            onChange={e => setForm({ ...form, name: e.target.value })}
                                            required
                                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-[#2E63E8] focus:ring-2 focus:ring-[#2E63E8]/20 outline-none transition-all"
                                        />
                                    </div>
                                    <div>
                                        <input
                                            type="tel"
                                            placeholder="Phone Number"
                                            value={form.phone}
                                            onChange={e => setForm({ ...form, phone: e.target.value })}
                                            required
                                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-[#2E63E8] focus:ring-2 focus:ring-[#2E63E8]/20 outline-none transition-all"
                                        />
                                    </div>
                                    <div>
                                        <select
                                            value={form.city}
                                            onChange={e => setForm({ ...form, city: e.target.value })}
                                            required
                                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-500 focus:border-[#2E63E8] focus:ring-2 focus:ring-[#2E63E8]/20 outline-none transition-all"
                                        >
                                            <option value="">Select Your City</option>
                                            {cities.map(c => (
                                                <option key={c} value={c}>{c}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <select
                                            value={form.service}
                                            onChange={e => setForm({ ...form, service: e.target.value })}
                                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-500 focus:border-[#2E63E8] focus:ring-2 focus:ring-[#2E63E8]/20 outline-none transition-all"
                                        >
                                            <option value="">Select Service (Optional)</option>
                                            {['Modular Kitchen', 'Wardrobe', 'TV Unit', 'False Ceiling', 'Full Home Interior'].map(s => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {error && <p className="text-red-500 text-xs text-center">{error}</p>}
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="btn-yellow w-full py-4 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? 'Sending...' : 'Book Free Consultation →'}
                                    </button>
                                    <p className="text-center text-gray-400 text-xs">
                                        🔒 100% free. No spam. We'll contact you within 1 hour.
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
